<template>
  <div v-if="dataLoaded">
    <!-- Row top buttons -->
    <div class="row q-mb-lg">
      <div class="col-6" style="justify-content: flex-start">
        <q-btn
          class="capitalize ctrl-button"
          color="grey-5"
          :label="`${$store.state.stringsStore.strings.action.toOverview}`"
          @click="$router.push(`/bookstore`)"
          icon="keyboard_backspace"
        />
      </div>

      <div class="col-6">
        <q-btn
          class="capitalize ctrl-button right q-ml-md"
          :color="readOnly ? 'grey-5' : 'ctrl-turquoise'"
          :label="readOnly ? getString('action.edit') : getString('action.save')"
          @click="saveChanges()"
          :icon-right="readOnly ? 'edit' : 'save'"
        />
        <q-btn
          v-if="data.id"
          class="q-ml-md capitalize ctrl-button right"
          icon-right="delete"
          color="negative capitalize"
          :label="$store.state.stringsStore.strings.action.delete"
          @click="deleteItem()"
          align="right"
        />
      </div>
    </div>

    <div class="row gutter-sm q-mb-lg">
      <!-- General Information -->
      <div class="col-md-6 col-sm-12 col-xs-12">
        <p class="card-title">
          {{ getString(null, 'Details', 'Details') }}
        </p>
        <div class="card-block">
          <!-- Name -->
          <q-field
            class="q-mb-md"
            :error="$v.data.name.$dirty && !$v.data.name.required"
            :error-label="getString(null, 'Name is required', 'Naam is verplicht')"
          >
            <q-input
              type="text"
              v-model.trim="$v.data.name.$model"
              :disable="readOnly"
              :float-label="getString('name')"
            >
            </q-input>
          </q-field>
          <!-- Email -->
          <q-field
            class="q-mb-md"
            :error="$v.data.email.$dirty && $v.data.email.$anyError"
            :error-label="
              !$v.data.email.required
                ? getString(null, 'Email is required', 'E-mail is verplicht')
                : getString(null, 'Invalid email', 'Ongeldig E-mail')
            "
          >
            <q-input
              type="text"
              v-model.trim="$v.data.email.$model"
              :disable="readOnly"
              :float-label="getString('email')"
            >
            </q-input>
          </q-field>
          <!-- Telephone -->
          <q-field class="q-mb-md">
            <q-input
              type="text"
              v-model="data.telephone"
              :disable="readOnly"
              :float-label="getString(null, 'Telephone', 'Telephone')"
            >
            </q-input>
          </q-field>
          <!-- Website -->
          <q-field class="q-mb-md">
            <q-input
              type="text"
              v-model="data.website"
              :disable="readOnly"
              :float-label="getString('website')"
            >
            </q-input>
          </q-field>
          <!-- Active -->
          <q-field class="q-mb-md">
            <span class="q-mr-md">{{ getString('active') }}</span>
            <q-toggle color="secondary" type="text" v-model="data.active" :disable="readOnly">
            </q-toggle>
          </q-field>
        </div>
      </div>
      <!-- Address Information -->
      <div class="col-md-6 col-sm-12 col-xs-12">
        <p class="card-title">
          {{ getString(null, 'Address', 'Adres') }}
        </p>
        <div class="card-block">
          <!-- Street -->
          <q-field
            class="q-mb-md"
            :error="$v.data.address.street.$dirty && !$v.data.address.street.required"
            :error-label="getString(null, 'Street is required', 'Straat is verplicht')"
          >
            <q-input
              type="text"
              v-model="$v.data.address.street.$model"
              :disable="readOnly"
              :float-label="getString(null, 'Street', 'Straat')"
            >
            </q-input>
          </q-field>
          <!-- Street number -->
          <q-field
            class="q-mb-md"
            :error="$v.data.address.street_number.$dirty && !$v.data.address.street_number.required"
            :error-label="
              getString(null, 'Street number is required', 'Straat nummer is verplicht')
            "
          >
            <q-input
              type="text"
              v-model="$v.data.address.street_number.$model"
              :disable="readOnly"
              :float-label="getString(null, 'Number', 'Straat nummer')"
            >
            </q-input>
          </q-field>
          <!-- Street number extension -->
          <q-field class="q-mb-md">
            <q-input
              type="text"
              v-model="data.address.street_number_extension"
              :disable="readOnly"
              :float-label="getString(null, 'Extension', 'Extensie')"
            >
            </q-input>
          </q-field>
          <!-- Postcode -->
          <q-field
            class="q-mb-md"
            :error="$v.data.address.postcode.$dirty && !$v.data.address.postcode.required"
            :error-label="getString(null, 'Postal code is required', 'Postcode is verplicht')"
          >
            <q-input
              type="text"
              v-model="$v.data.address.postcode.$model"
              :disable="readOnly"
              :float-label="getString(null, 'Postal code', 'Postcode')"
            >
            </q-input>
          </q-field>
          <!-- City -->
          <q-field
            class="q-mb-md"
            :error="$v.data.address.city.$dirty && !$v.data.address.city.required"
            :error-label="getString(null, 'Postal code is required', 'Postcode is verplicht')"
          >
            <q-input
              type="text"
              v-model="$v.data.address.city.$model"
              :disable="readOnly"
              :float-label="getString(null, 'City', 'Stad')"
            >
            </q-input>
          </q-field>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { required, email } from 'vuelidate/lib/validators';
import saveChanges from '@/mixins/detailViews/saveChanges';
import suggestUrl from '@/mixins/suggestUrl';
import getString from '@/helpers/GetStrings';

export default {
  validations: {
    data: {
      name: {
        required,
      },
      email: {
        required,
        email,
      },
      address: {
        city: {
          required,
        },
        street: {
          required,
        },
        street_number: {
          required,
        },
        postcode: {
          required,
        },
      },
    },
  },
  data() {
    return {
      successMessage: this.getString(
        null,
        'The bookstore has been saved',
        'Het boekenwinkel is succesvol opgeslagen',
      ),
      dataLoaded: false,
      endpoint: 'bookstore',
      responseData: {
        multiple: 'bookstores',
        single: 'bookstore',
      },
      module: 'bookstore',
      data: {
        name: '',
        email: '',
        telephone: '',
        website: '',
        active: true,
        address: {
          city: '',
          street: '',
          street_number: '',
          street_number_extension: '',
          postcode: '',
        },
      },
    };
  },

  mixins: [suggestUrl, saveChanges],

  methods: {
    getString,

    requestParams() {
      const requiredFields = {
        name: this.data.name,
        email: this.data.email,
        telephone: this.data.telephone,
        website: this.data.website,
        city: this.data.address.city,
        street: this.data.address.street,
        street_number: this.data.address.street_number,
        postcode: this.data.address.postcode,
        active: Number(this.data.active),
      };

      const optional = {
        street_number_extension: this.data.address.street_number_extension,
      };

      // If a value is null, undefined or empty, remove it from fields
      for (const [key, value] of Object.entries(optional)) {
        if (value === null || value === undefined) delete optional[key];
      }

      // Join required and optional
      const fields = Object.assign(requiredFields, optional);

      return fields;
    },

    deleteItem() {
      // Dialog
      this.$q
        .dialog({
          title: '',
          message: this.getString('module.library.delete.title.single'),
          ok: this.$store.state.stringsStore.strings.dialog.confirm.ok,
          cancel: this.$store.state.stringsStore.strings.dialog.confirm.cancel,
        })
        .then(() => {
          // delete call
          this.axios
            .delete(`bookstore/${this.data.id}`)
            .then(() => {
              // Refresh data to see the result
              this.$q.notify({
                message: this.getString('upload.delete.success').replace(
                  '%file%',
                  this.getString('bookstore'),
                ),
                position: 'top-right',
                timeout: 1000,
                color: 'positive',
              });
              this.$router.push('/bookstore');
            })
            .catch((error) => {
              // Error
              console.warn(error);
            });
        })
        .catch(() => {
          console.warn('Not removed');
        });
    },
  },

  mounted() {
    if (this.$route.params.id) this.readOnly = true;
  },

  created() {
    if (this.$route.params.id) {
      // Fetch the selected module details by id.
      this.axios
        .get(`bookstore/${this.$route.params.id}`)
        .then((response) => {
          // In the module detail page, if a given id is not found, we will always get a 200 OK response.
          if (!response.data.bookstore) {
            // Redirect to modules overview
            this.$router.push('/bookstore');
            return;
          }

          this.data = response.data.bookstore;

          this.dataLoaded = true;
        })
        .catch((error) => {
          console.warn(error);

          // Notify if nothing found
          if (error.response.status === 404) {
            this.$q.notify({
              message: getString(`module.${this.module}.edit.error.nothingFound`),
              type: 'warning',
              timeout: 2000,
              position: 'top-right',
            });
          } else {
            // Throw general error notification
            this.$q.notify({
              message: getString('error.general'),
              position: 'top-right',
              timeout: 1000,
              type: 'warning',
            });
          }

          // Redirect to modules overview
          this.$router.push(`/${this.module}`);
        });
    } else {
      // Nothing to load
      this.dataLoaded = true;
    }
  },
};
</script>

<style lang="stylus" scoped>
@import '~variables';
.data-title
  font-weight 700

.data-content
  word-break break-all
</style>
